import { brand, neutral, highlight, semantic, font, radius, shadow } from './tokens';

/**
 * Ant Design theme built from the Shelf tokens. Styling antd through tokens (instead of
 * `!important` CSS overrides) keeps every component — tables, forms, modals, menus — consistent.
 */
const antdTheme = {
    token: {
        colorPrimary: brand[600],
        colorInfo: brand[600],
        // Info boxes (<Alert type="info">, info tags): light blue like the other banners. Derived
        // from the navy colorInfo they came out dark grey.
        colorInfoBg: highlight.bg,
        colorInfoBorder: highlight.border,
        colorSuccess: semantic.success.solid,
        colorWarning: semantic.warning.solid,
        colorError: semantic.danger.solid,
        colorLink: brand[600],
        colorText: neutral[900],
        colorTextSecondary: neutral[500],
        colorTextTertiary: neutral[400],
        colorTextQuaternary: neutral[300],
        colorBorder: neutral[200],
        colorBorderSecondary: neutral[100],
        colorSplit: neutral[100],
        colorBgLayout: neutral[50],
        colorBgContainer: neutral[0],
        colorFillAlter: neutral[50],
        colorFillSecondary: neutral[100],
        fontFamily: font.sans,
        fontSize: 14,
        fontSizeHeading1: 30,
        fontSizeHeading2: 24,
        fontSizeHeading3: 20,
        fontSizeHeading4: 16,
        fontSizeHeading5: 14,
        fontWeightStrong: 600,
        lineHeight: 1.5,
        borderRadius: radius.md,
        borderRadiusSM: radius.sm,
        borderRadiusLG: radius.lg,
        borderRadiusXS: 4,
        controlHeight: 36,
        controlHeightLG: 40,
        controlHeightSM: 28,
        controlOutline: 'rgba(47, 128, 247, 0.18)',
        controlOutlineWidth: 3,
        boxShadow: shadow.md,
        boxShadowSecondary: shadow.lg,
        boxShadowTertiary: shadow.xs,
        motionDurationMid: '0.18s',
        wireframe: false
    },
    components: {
        Layout: { bodyBg: neutral[50], headerBg: 'transparent', siderBg: neutral[0] },
        Card: {
            borderRadiusLG: radius.lg,
            paddingLG: 20,
            headerFontSize: 15,
            headerHeight: 52,
            boxShadowTertiary: shadow.xs
        },
        Button: {
            fontWeight: 500,
            primaryShadow: 'none',
            defaultShadow: 'none',
            dangerShadow: 'none',
            defaultBorderColor: neutral[200],
            defaultColor: neutral[800],
            paddingInline: 14
        },
        Table: {
            headerBg: neutral[50],
            headerColor: neutral[500],
            headerSplitColor: 'transparent',
            headerBorderRadius: radius.lg,
            rowHoverBg: neutral[25],
            borderColor: neutral[100],
            cellPaddingBlock: 12,
            cellPaddingInline: 16,
            cellPaddingBlockSM: 8,
            cellPaddingBlockMD: 10,
            footerBg: neutral[50]
        },
        Menu: {
            itemHeight: 36,
            itemBorderRadius: radius.md,
            itemMarginInline: 8,
            itemMarginBlock: 2,
            itemColor: neutral[600],
            itemHoverColor: neutral[900],
            itemHoverBg: neutral[100],
            itemSelectedBg: brand[50],
            itemSelectedColor: brand[700],
            itemActiveBg: brand[100],
            subMenuItemBg: 'transparent',
            iconSize: 16,
            collapsedIconSize: 18,
            iconMarginInlineEnd: 12,
            groupTitleColor: neutral[400],
            groupTitleFontSize: 11
        },
        Tabs: { itemColor: neutral[500], itemSelectedColor: neutral[900], inkBarColor: brand[600], titleFontSize: 14 },
        Segmented: { itemSelectedBg: neutral[0], trackBg: neutral[100], borderRadius: radius.md },
        Tag: { defaultBg: neutral[100], defaultColor: neutral[700], borderRadiusSM: radius.sm },
        Modal: { borderRadiusLG: radius.xl, titleFontSize: 16, headerBg: neutral[0], contentBg: neutral[0] },
        Drawer: { colorBgElevated: neutral[0] },
        Input: { activeShadow: shadow.focus, hoverBorderColor: neutral[300], paddingInline: 12 },
        InputNumber: { activeShadow: shadow.focus, hoverBorderColor: neutral[300] },
        Select: { optionSelectedBg: brand[50], optionSelectedFontWeight: 500, optionPadding: '7px 12px', optionActiveBg: neutral[50] },
        DatePicker: { activeShadow: shadow.focus, hoverBorderColor: neutral[300] },
        Dropdown: { paddingBlock: 8, controlItemBgHover: neutral[50] },
        Tooltip: { colorBgSpotlight: neutral[900], borderRadius: radius.md },
        Statistic: { titleFontSize: 13, contentFontSize: 26 },
        Progress: { defaultColor: brand[600], remainingColor: neutral[100] },
        Alert: { borderRadiusLG: radius.lg, withDescriptionPadding: '14px 16px' },
        Pagination: { itemActiveBg: brand[50] },
        Badge: { textFontWeight: 600 },
        Collapse: { headerBg: neutral[50], borderRadiusLG: radius.lg },
        Empty: { colorTextDescription: neutral[400] }
    }
};

export default antdTheme;
